import { Controller } from "@hotwired/stimulus"

// Collects form data from all individual procedure step forms and submits
// them as a single POST request to the save_all_steps endpoint. This avoids
// the timing and DOM-replacement issues that arise from clicking each step's
// "Mark Complete" button sequentially.
export default class extends Controller {
  static targets = ["submitButton", "changeReasonModal", "changeReasonSelect", "confirmReasonButton"]
  static values = { url: String, metaFormSelector: String, samplesShipped: Boolean }

  async submitAll(event) {
    event.preventDefault()

    const button = this.submitButtonTarget
    const originalHTML = button.innerHTML

    const disableButton = () => {
      button.disabled = true
      button.innerHTML = '<i class="fa-solid fa-spinner fa-spin mr-1"></i> Saving...'
    }

    const restoreButton = () => {
      button.disabled = false
      button.innerHTML = originalHTML
    }

    disableButton()

    const metaForm = document.querySelector(this.metaFormSelectorValue)
    const stepForms = Array.from(
      document.querySelectorAll('turbo-frame[id^="procedure_step_"] form')
    )

    const allForms = [metaForm, ...stepForms].filter(Boolean)

    for (const form of allForms) {
      if (!form.reportValidity()) {
        restoreButton()
        return
      }
    }

    const mergedFormData = this._buildMergedFormData(metaForm, stepForms)

    if (this.samplesShippedValue) {
      this._pendingFormData = mergedFormData
      this._restoreButton = restoreButton
      this._showChangeReasonModal()
      return
    }

    await this._submit(mergedFormData, restoreButton)
  }

  async submitWithReason(event) {
    const reason = this.changeReasonSelectTarget.value
    if (!reason) return

    $(this.changeReasonModalTarget).modal('hide')

    this._pendingFormData.set('change_reason', reason)
    const formData = this._pendingFormData
    const restoreButton = this._restoreButton
    this._pendingFormData = null
    this._restoreButton = null

    await this._submit(formData, restoreButton)
  }

  toggleConfirmButton() {
    const hasReason = this.changeReasonSelectTarget.value !== ''
    this.confirmReasonButtonTarget.disabled = !hasReason
  }

  _showChangeReasonModal() {
    this.changeReasonSelectTarget.value = ''
    this.toggleConfirmButton()

    $(this.changeReasonModalTarget).modal('show')
    $(this.changeReasonModalTarget).one('hidden.bs.modal', () => {
      if (this._pendingFormData) {
        this._pendingFormData = null
        this._restoreButton?.()
        this._restoreButton = null
      }
    })
  }

  _barcodeChanged() {
    const inputs = document.querySelectorAll('[data-original-barcode-value]')
    return Array.from(inputs).some(input =>
      input.value !== (input.dataset.originalBarcodeValue ?? '')
    )
  }

  _buildMergedFormData(metaForm, stepForms) {
    const mergedFormData = new FormData()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (csrfToken) {
      mergedFormData.append('authenticity_token', csrfToken)
    }

    // Use `set` (not `append`) so shared hidden fields (sample_timepoint_id,
    // selected_language, etc.) collapse to a single scalar instead of
    // repeating per-form, which Rails would parse as an array.
    if (metaForm) {
      const metaData = new FormData(metaForm)
      for (const [key, value] of metaData.entries()) {
        if (key === 'authenticity_token') continue
        mergedFormData.set(key, value)
      }
    }

    for (const form of stepForms) {
      const formData = new FormData(form)
      for (const [key, value] of formData.entries()) {
        if (key === 'authenticity_token') continue
        mergedFormData.set(key, value)
      }
    }

    return mergedFormData
  }

  async _submit(mergedFormData, restoreButton) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        body: mergedFormData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'same-origin'
      })

      if (response.redirected) {
        Turbo.visit(response.url)
      } else if (response.ok) {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.startsWith('text/vnd.turbo-stream')) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        } else {
          Turbo.visit(window.location.href, { action: 'replace' })
        }
      } else {
        restoreButton?.()
      }
    } catch (error) {
      console.error('Error saving all steps:', error)
      restoreButton?.()
    }
  }
}
